/**
 * Timezone utilities for RutaLink multi-tenant SaaS
 * Provides hierarchical timezone resolution: user → tenant → system default
 */

import { fromZonedTime, toZonedTime, format as formatTz } from 'date-fns-tz';
import { isValid } from 'date-fns';
import type { Locale } from 'date-fns';

// Default timezone for the system (Central Time for Dallas, TX)
export const DEFAULT_TIMEZONE = 'America/Chicago';

// Common IANA timezone identifiers for business use
export const COMMON_TIMEZONES = {
  // US Timezones
  'America/New_York': 'Eastern Time',
  'America/Chicago': 'Central Time', 
  'America/Denver': 'Mountain Time',
  'America/Los_Angeles': 'Pacific Time',
  
  // Mexico
  'America/Mexico_City': 'Mexico Central Time',
  'America/Cancun': 'Mexico Eastern Time',
  'America/Tijuana': 'Mexico Pacific Time',
  
  // Canada
  'America/Toronto': 'Eastern Time (Canada)',
  'America/Vancouver': 'Pacific Time (Canada)',
  
  // Other Americas
  'America/Sao_Paulo': 'Brazil Time',
  
  // UTC
  'UTC': 'Coordinated Universal Time'
} as const;

export type SupportedTimezone = keyof typeof COMMON_TIMEZONES;

/**
 * Validates if a timezone string is a valid IANA timezone
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    // Test with a known date to see if timezone is valid
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Resolves the effective timezone for a user using hierarchical fallback:
 * 1. User's personal timezone preference
 * 2. Tenant's default timezone  
 * 3. System default timezone (America/Chicago)
 */
export function resolveUserTimezone(
  userTimezone?: string | null,
  tenantTimezone?: string | null
): string {
  // Try user timezone first
  if (userTimezone && isValidTimezone(userTimezone)) {
    return userTimezone;
  }
  
  // Fall back to tenant timezone
  if (tenantTimezone && isValidTimezone(tenantTimezone)) {
    return tenantTimezone;
  }
  
  // Final fallback to system default
  return DEFAULT_TIMEZONE;
}

/**
 * Gets display name for a timezone
 */
export function getTimezoneDisplayName(timezone: string): string {
  if (timezone in COMMON_TIMEZONES) {
    return COMMON_TIMEZONES[timezone as SupportedTimezone];
  }
  
  // For unknown timezones, try to generate a readable name
  try {
    const now = new Date();
    const formatted = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'long'
    }).formatToParts(now);
    
    const timeZoneName = formatted.find(part => part.type === 'timeZoneName')?.value;
    return timeZoneName || timezone;
  } catch {
    return timezone;
  }
}

/**
 * Gets timezone abbreviation (e.g., CDT, EST, PST)
 */
export function getTimezoneAbbreviation(timezone: string, date?: Date): string {
  try {
    const targetDate = date || new Date();
    const formatted = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    }).formatToParts(targetDate);
    
    const abbreviation = formatted.find(part => part.type === 'timeZoneName')?.value;
    return abbreviation || timezone;
  } catch {
    return timezone;
  }
}

/**
 * Converts UTC date to specific timezone
 */
export function convertUtcToTimezone(utcDate: Date | string, timezone: string): Date {
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  
  if (!isValid(date)) {
    throw new Error('Invalid date provided');
  }
  
  if (!isValidTimezone(timezone)) {
    throw new Error(`Invalid timezone: ${timezone}`);
  }
  
  return toZonedTime(date, timezone);
}

/**
 * Converts timezone-specific date to UTC
 */
export function convertTimezoneToUtc(localDate: Date | string, timezone: string): Date {
  const date = typeof localDate === 'string' ? new Date(localDate) : localDate;
  
  if (!isValid(date)) {
    throw new Error('Invalid date provided');
  }
  
  if (!isValidTimezone(timezone)) {
    throw new Error(`Invalid timezone: ${timezone}`);
  }
  
  return fromZonedTime(date, timezone);
}

/**
 * Formats a date in a specific timezone with optional format string
 */
export function formatInTimezone(
  date: Date | string,
  timezone: string,
  formatString: string = 'MMM d, yyyy h:mm a',
  locale?: Locale
): string {
  const targetDate = typeof date === 'string' ? new Date(date) : date;
  
  if (!isValid(targetDate)) {
    return 'Invalid Date';
  }
  
  if (!isValidTimezone(timezone)) {
    return 'Invalid Timezone';
  }
  
  try {
    // date-fns-tz `format` does NOT shift the wall clock for h:mm/date tokens —
    // it reads the Date's fields in the host's system timezone (the `timeZone`
    // option only drives offset/zone tokens like z/XXX). We must convert the UTC
    // instant into a zoned Date first, otherwise server-side output renders in the
    // process TZ (UTC on Railway → off by the tenant's offset). A Central-time dev
    // machine masks this because system TZ happens to match the tenant.
    const zoned = toZonedTime(targetDate, timezone);
    return formatTz(zoned, formatString, { timeZone: timezone, ...(locale && { locale }) });
  } catch {
    return 'Format Error';
  }
}

/**
 * Formats a date with timezone abbreviation for user display
 */
export function formatWithTimezone(
  date: Date | string,
  timezone: string,
  formatString: string = 'MMM d, yyyy h:mm a'
): string {
  const formattedDate = formatInTimezone(date, timezone, formatString);
  const abbreviation = getTimezoneAbbreviation(timezone, new Date(date));
  
  return `${formattedDate} ${abbreviation}`;
}

/**
 * Creates a timezone-aware Date object for form inputs
 * Useful for datetime-local inputs that need timezone context
 */
export function createTimezoneAwareDate(
  dateString: string,
  timezone: string
): Date {
  if (!dateString) {
    return new Date();
  }
  
  // If the date string doesn't include timezone info, treat it as being in the specified timezone
  const date = new Date(dateString);
  
  if (!isValid(date)) {
    return new Date();
  }
  
  // If the date is already timezone-aware (has 'Z' or timezone offset), return as-is
  if (dateString.includes('Z') || dateString.match(/[+-]\d{2}:\d{2}$/)) {
    return date;
  }
  
  // Otherwise, treat it as a local time in the specified timezone and convert to UTC
  return fromZonedTime(date, timezone);
}

/**
 * Gets a list of common timezones for UI dropdowns
 */
export function getCommonTimezones(): Array<{ value: string; label: string; group?: string }> {
  return [
    // US Timezones
    { value: 'America/New_York', label: 'Eastern Time', group: 'United States' },
    { value: 'America/Chicago', label: 'Central Time', group: 'United States' },
    { value: 'America/Denver', label: 'Mountain Time', group: 'United States' },
    { value: 'America/Los_Angeles', label: 'Pacific Time', group: 'United States' },
    
    // Mexico
    { value: 'America/Mexico_City', label: 'Mexico Central Time', group: 'Mexico' },
    { value: 'America/Cancun', label: 'Mexico Eastern Time', group: 'Mexico' },
    { value: 'America/Tijuana', label: 'Mexico Pacific Time', group: 'Mexico' },
    
    // Canada
    { value: 'America/Toronto', label: 'Eastern Time (Canada)', group: 'Canada' },
    { value: 'America/Vancouver', label: 'Pacific Time (Canada)', group: 'Canada' },
    
    // Other
    { value: 'America/Sao_Paulo', label: 'Brazil Time', group: 'South America' },
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)', group: 'UTC' }
  ];
}

/**
 * User context interface for timezone resolution
 */
export interface UserTimezoneContext {
  userTimezone?: string | null;
  tenantTimezone?: string | null;
  resolvedTimezone: string;
  abbreviation: string;
}

/**
 * Creates a user timezone context with resolved timezone
 */
export function createUserTimezoneContext(
  userTimezone?: string | null,
  tenantTimezone?: string | null
): UserTimezoneContext {
  const resolvedTimezone = resolveUserTimezone(userTimezone, tenantTimezone);
  const abbreviation = getTimezoneAbbreviation(resolvedTimezone);
  
  return {
    userTimezone,
    tenantTimezone,
    resolvedTimezone,
    abbreviation
  };
}
/**
 * UTC [start, end) instants bounding one calendar day (yyyy-MM-dd) in an
 * IANA timezone. Half-open on purpose — chain `gte(start)` + `lt(end)` and
 * adjacent days can never overlap or leave a gap. DST-safe: each boundary
 * is resolved through the timezone, so a spring-forward / fall-back day
 * still spans the correct 23h/25h window.
 */
export function dayBoundsUtc(
  dateStr: string,
  timezone?: string | null
): { dayStart: Date; dayEnd: Date } {
  const tz = timezone || DEFAULT_TIMEZONE;
  // Advance one calendar day in UTC space to get the next date string; the
  // conversion back through the timezone below re-applies the correct offset.
  const tomorrow = new Date(`${dateStr}T00:00:00Z`);
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  const tomorrowStr = tomorrow.toISOString().slice(0, 10);
  return {
    dayStart: convertTimezoneToUtc(new Date(`${dateStr}T00:00:00`), tz),
    dayEnd: convertTimezoneToUtc(new Date(`${tomorrowStr}T00:00:00`), tz),
  };
}

/**
 * `dayBoundsUtc` for "today" as seen from the given timezone. The shared
 * tenant-day-boundary helper for KPI counters, caller-context "today", and
 * any other tenant-calendar-day query (one implementation — the boundary
 * math was previously duplicated with disagreeing bounds).
 */
export function tenantDayBoundsUtc(
  now: Date,
  timezone?: string | null
): { dayStart: Date; dayEnd: Date } {
  const tz = timezone || DEFAULT_TIMEZONE;
  return dayBoundsUtc(formatInTimezone(now, tz, 'yyyy-MM-dd'), tz);
}
